/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {KeyboardEvent, memo, MouseEvent, useCallback} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import Chip from '../components/chip/Chip';
import {WarningIcon} from '../icons/current/WarningIcon';
import {MaterialExitCloseIcon} from '../icons/MaterialExitCloseIcon';
import {dlsColor} from '../theme/theme';
import useThemeContext from '../theme/useThemeContext';
import {IconSize} from '../types/types';
import {AppliedFilterBarItem} from './AppliedFilterBarTypes';

export interface AppliedFilterChipProps {
  className: string;
  closeIconClassName: string;
  item: AppliedFilterBarItem;
}

// the keys that remove a focused chip, as they do a Material UI chip ...
const DELETE_KEYS = ['Backspace', 'Delete'];

// the current theme chip's own states - its look otherwise comes from the
// filter bar's chip class, which styles it through the Material UI chip's own
// label and delete icon class names. A tag keeps its look on hover, per the
// DLS - only keyboard focus marks it ...
const useStyles = makeStyles(() => ({
  chip : {
    cursor            : 'pointer',
    outline           : 'none',
    '&:focus-visible' : {
      background : `${dlsColor('aquamarine')} !important`,
      border     : `1.5px solid ${dlsColor('skyBlue')} !important`
    }
  },
  deleteIcon : {
    cursor : 'pointer'
  },
  // the DLS warning tag (filter/applied/Error) - a pale orange tag, the
  // theme's orange faint behind its close button, with a warning icon after
  // the label. Tripled to outrank the filter bar's own (doubled) chip rule ...
  warning : {
    '&&&' : {
      backgroundColor : () => dlsColor('paleOrangeBackground')
    },
    '&&& .MuiChip-deleteIcon, &&& .MuiChip-deleteIcon > span' : {
      backgroundColor : () => `${dlsColor('orange')}26`
    }
  },
  warningIcon : {
    display    : 'flex',
    flexShrink : 0
  }
}));

/**
 * Displays the current theme's applied filter chip - plain markup under one
 * shared style sheet, rather than a Material UI chip whose prop-driven styles
 * build a style sheet of its own for every chip, which made a bar (and its
 * "+N More" modal) of hundreds of chips slow to draw.
 */
const CurrentAppliedFilterChip = ({
  className,
  closeIconClassName,
  item
}: AppliedFilterChipProps) => {
  const classes    = useStyles();
  const {onDelete} = item;
  const onSelect   = item.onClick ?? onDelete;

  const onDeleteClick = useCallback(function(event: MouseEvent) {
    event.stopPropagation();
    onDelete();
  }, [onDelete]);

  const onKeyDown = useCallback(function(event: KeyboardEvent) {
    if (DELETE_KEYS.includes(event.key)) {
      event.preventDefault();
      onDelete();
    } else if (event.key === 'Enter') {
      onSelect();
    }
  }, [onDelete, onSelect]);

  return (
    <div id={`applied-filter-${item.id}`}
      className={classnames('MuiChip-root', className, classes.chip, {[classes.warning]: item.isWarning})}
      role='button'
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={onKeyDown}>
      {item.icon}
      <span className='MuiChip-label'>
        {item.label}
      </span>
      {item.isWarning && (
        <span className={classes.warningIcon}>
          <WarningIcon size={IconSize.small} />
        </span>
      )}
      <span className={classnames('MuiChip-deleteIcon', classes.deleteIcon)}
        role='button'
        aria-label={translate('common:delete')}
        onClick={onDeleteClick}>
        <span className={closeIconClassName}>
          <MaterialExitCloseIcon color={dlsColor('grey2')} size={IconSize.small} />
        </span>
      </span>
    </div>
  );
};

/**
 * Displays one applied filter chip with the current filter-bar close affordance.
 */
export const AppliedFilterChip = ({
  className,
  closeIconClassName,
  item
}: AppliedFilterChipProps) => {
  const {isCurrent} = useThemeContext();
  return isCurrent ? (
    <CurrentAppliedFilterChip className={className}
      closeIconClassName={closeIconClassName}
      item={item} />
  ) : (
    <Chip id={`applied-filter-${item.id}`}
      label={item.label}
      icon={item.icon as any}
      size='small'
      maxWidth={false}
      borderRadius='4px'
      labelColor={dlsColor('grey2')}
      hoverDefault
      showCustomDeleteIcon
      noTextTransform
      className={className}
      customDeleteIcon={(
        <span className={closeIconClassName}>
          <MaterialExitCloseIcon color={dlsColor('grey2')} size={IconSize.small} />
        </span>
      )}
      onClick={item.onClick ?? item.onDelete}
      onDelete={item.onDelete} />
  );
};

export default memo(AppliedFilterChip);
